import { MediaText, TextValueFormat } from 'editor/src/store/design/types';

import updateTextPropertiesWithoutRender from './updateTextPropertiesWithoutRender';

/**
 * The update that carries a shared value onto one member: the string verbatim,
 * re-measured for that member's own box and font.
 *
 * Verbatim, not re-poured through the member's own `valueFormat`: one shared
 * value means both sides of the card read identically, and the string arriving
 * here has already been formatted on the element the buyer typed into.
 *
 * The format travels with it, which is what makes "verbatim" safe. A group
 * holds one value and therefore one format: were the front masked and the back
 * not, the stored string would depend on which side the buyer happened to type
 * on — and it is the stored string the print file is generated from, so the
 * masked side would print raw digits.
 *
 * Kept out of sharedTextFields, which the buyer's personalizer imports, so the
 * query half stays free of measurement. Note this is separation, NOT a bundle
 * guarantee: updateMediaElementOperation already imports the measuring code
 * statically and the personalizer's field imports that operation, so it reaches
 * that bundle regardless — the `import()` next to it there has been moot for
 * longer than this module has existed. Worth untangling; not claimed here.
 */
const buildSharedTextValueUpdate = (
  element: MediaText,
  text: string,
  sampleFlagChangeAllowed: boolean,
  valueFormat: TextValueFormat | undefined,
): Partial<MediaText> => {
  const elementUpdate: Partial<MediaText> = { extra: { ...element.extra, text, valueFormat } };
  updateTextPropertiesWithoutRender(element, elementUpdate, undefined);
  // The same two clean-ups the buyer's own keystroke gets in
  // updateMediaElementOperation: a mirrored value is real content, so the
  // element stops being a sample, and a custom font's cached glyph outline no
  // longer matches the string it was baked from.
  //
  // The sample clear drops one condition the original carries — in embedded
  // mode it clears only when the text actually changed. Propagation reaches
  // here with the text unchanged on a format-only edit (a mask added to the
  // group), and there this would clear a sample flag the buyer's own typing
  // would have left alone. Left as is rather than threaded through: it needs
  // both an embedded host and a mask edited after the group formed, and the
  // flag only marks placeholder content the first real value clears anyway.
  if (!sampleFlagChangeAllowed && element.sample) {
    elementUpdate.sample = undefined;
  }
  // The path clear keeps its condition: unchanged text means the cached outline
  // still matches, and dropping it would cost a regeneration for nothing.
  if (element.extra.fontFamily?.startsWith('custom-') && text !== element.extra.text) {
    elementUpdate.path = undefined;
  }
  return elementUpdate;
};

export default buildSharedTextValueUpdate;
